import { useState, useEffect, useCallback, useMemo } from 'react';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const useStoriesFeed = (view) => {
  const isDayFeed = view === 'all';
  const [pages, setPages] = useState([]); // day-bucketed pages for the 'all' view: [{dayOffset, date, stories}]
  const [staticStories, setStaticStories] = useState([]); // flat list for bubble/blindsyde/timeline views
  const [hotStories, setHotStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);

  const fetchStories = useCallback(async (silentRefresh = false) => {
    try {
      if (!silentRefresh) setLoading(true);
      setError(null);
      const hotEndpoint = `${BACKEND_URL}/api/stories/hot`;

      if (isDayFeed) {
        const [feedRes, hotRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/stories/feed?day_offset=0`),
          fetch(hotEndpoint),
        ]);
        if (!feedRes.ok || !hotRes.ok) throw new Error('Failed to fetch stories');
        const feedData = await feedRes.json();
        setHotStories(await hotRes.json());
        setPages((prev) => {
          const rest = silentRefresh ? prev.filter((p) => p.dayOffset !== 0) : [];
          if (rest.length === 0) setHasMore(feedData.has_more);
          return [{ dayOffset: 0, date: feedData.date, stories: feedData.stories }, ...rest];
        });
      } else {
        let mainEndpoint = `${BACKEND_URL}/api/stories/main`;
        if (view === 'bubble') mainEndpoint = `${BACKEND_URL}/api/stories/main?bubble=true`;
        else if (view === 'blindsyde') mainEndpoint = `${BACKEND_URL}/api/stories/blindsyde`;
        else if (view === 'timeline') mainEndpoint = `${BACKEND_URL}/api/stories/timeline`;

        const [mainResponse, hotResponse] = await Promise.all([
          fetch(mainEndpoint),
          fetch(hotEndpoint),
        ]);
        if (!mainResponse.ok || !hotResponse.ok) throw new Error('Failed to fetch stories');
        setStaticStories(await mainResponse.json());
        setHotStories(await hotResponse.json());
        setHasMore(false);
      }
    } catch (err) {
      logError('useStoriesFeed:fetchStories', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [view, isDayFeed]);

  const loadMore = useCallback(async () => {
    if (!isDayFeed || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextOffset = pages.length;
      const res = await fetch(`${BACKEND_URL}/api/stories/feed?day_offset=${nextOffset}`);
      if (res.ok) {
        const data = await res.json();
        setPages((prev) => [...prev, { dayOffset: nextOffset, date: data.date, stories: data.stories }]);
        setHasMore(data.has_more);
      }
    } catch (err) {
      logError('useStoriesFeed:loadMore', err);
    } finally {
      setLoadingMore(false);
    }
  }, [isDayFeed, loadingMore, hasMore, pages.length]);

  useEffect(() => {
    setPages([]);
    fetchStories();
    const intervalId = setInterval(() => fetchStories(true), REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [view, fetchStories]);

  const dayGroups = useMemo(
    () => [...pages].sort((a, b) => a.dayOffset - b.dayOffset),
    [pages]
  );
  const mainStories = useMemo(
    () => (isDayFeed ? dayGroups.flatMap((p) => p.stories) : staticStories),
    [isDayFeed, dayGroups, staticStories]
  );

  return {
    mainStories, dayGroups: isDayFeed ? dayGroups : null, hotStories,
    loading, error, hasMore, loadingMore, loadMore, refetch: fetchStories,
  };
};
